import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { api, type AuthKind, type BodyType, type KV, type RunResult, type TestResult, type User } from "../lib/api";
import Spinner, { LoadingButton, InlineLoading } from "./Spinner";
import VarText from "./VarText";
import RevisionDetailModal from "./RevisionDetailModal";
import RelativeTime from "./RelativeTime";
import { copyToClipboard } from "./Toast";
import AuthEditor from "./AuthEditor";
import MultipartBodyEditor from "./MultipartBodyEditor";
import GraphQLBodyEditor from "./GraphQLBodyEditor";
import MethodPicker from "./MethodPicker";
import JsonCodeEditor from "./JsonCodeEditor";
import CodeViewer, { detectLangFromContentType, tryPrettyJson, type SyntaxLang } from "./CodeViewer";
import { useConfirm } from "./ConfirmDialog";
import Markdown from "./Markdown";
import Comments from "./Comments";
import Avatar from "./Avatar";
import { emit as emitCurl, type RequestSpec as CurlSpec } from "@rest/curl";
import { evalJsonPath } from "../lib/jsonPath";
import CodeExport from "./CodeExport";
import type { RequestSpec as CodegenSpec } from "@rest/codegen";
import { EnvDot } from "../pages/Environments";
import ChipInput from "./ChipInput";
import RunDetailModal from "./RunDetailModal";
import { findVars, interpolate } from "../lib/envInterp";
import { readExecutor, readAgentId, readEnvId } from "./Topbar";
import { useResizable, useMediaQuery } from "../lib/useResizable";
import { useHotkey } from "../lib/keybindings";
import { closeOpenTabFor } from "../lib/openTabs";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

type Tab = "params" | "headers" | "body" | "auth" | "docs" | "comments" | "revisions" | "runs";

interface RunExtras { tests?: TestResult[]; scriptLog?: unknown[] }

export default function RequestBuilder({ requestId, workspaceId }: { requestId: string; workspaceId: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const detailQ = useQuery({
    queryKey: ["request", requestId],
    queryFn: () => api.requests.get(requestId),
  });
  const data = detailQ.data?.request ?? null;

  // Agents + environments + me for the run/comments features.
  const agentsQ = useQuery({
    queryKey: ["agents", workspaceId],
    queryFn: () => api.agents.list(workspaceId),
  });
  const envsQ = useQuery({
    queryKey: ["environments", workspaceId],
    queryFn: () => api.environments.list(workspaceId),
  });
  const meQ = useQuery({ queryKey: ["me"], queryFn: () => api.me() });
  const me: User | null = meQ.data?.user ?? null;

  // Local editor state — initialized from the loaded request, then editable.
  const [name, setName] = useState("");
  const [method, setMethod] = useState("GET");
  const [url, setUrl] = useState("");
  const [headers, setHeaders] = useState<KV[]>([]);
  const [query, setQuery] = useState<KV[]>([]);
  const [bodyType, setBodyType] = useState<BodyType>("none");
  const [body, setBody] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [docs, setDocs] = useState<string>("");
  const [preScript, setPreScript] = useState<string>("");
  const [postScript, setPostScript] = useState<string>("");
  const [testsScript, setTestsScript] = useState<string>("");
  const [authKind, setAuthKind] = useState<AuthKind>("none");
  const [authFields, setAuthFields] = useState<Record<string, string>>({});
  const [savedAt, setSavedAt] = useState<string | null>(null);
  // Tab + env are URL-driven so they're deep-linkable. Falling back to the
  // first time we render with no query param lands on the default tab.
  const [sp, setSp] = useSearchParams();
  const tab: Tab = ((): Tab => {
    const q = sp.get("tab") ?? "";
    // Legacy "history" links land on Revisions (the more common reason
    // people opened History pre-split). Bookmarks + collection palette
    // results stay working.
    if (q === "history") return "revisions";
    const valid: Tab[] = ["params", "headers", "body", "auth", "docs", "comments", "revisions", "runs"];
    return (valid as string[]).includes(q) ? (q as Tab) : "params";
  })();
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(sp);
    if (t === "params") next.delete("tab"); else next.set("tab", t);
    setSp(next, { replace: true });
  };
  const [response, setResponse] = useState<(RunResult & RunExtras) | null>(null);
  // Executor + agent live in localStorage so they're workspace-wide, set
  // from the Top bar. Env is URL-driven (?env=…).
  const executor = readExecutor(workspaceId);
  const agentId = readAgentId(workspaceId);
  const envId = sp.get("env") || readEnvId(workspaceId);
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const [codeOpen, setCodeOpen] = useState(false);

  // Renames hit the API directly (no editable name input here), so we
  // pull the latest server name into local state whenever it changes.
  // The bulk reset below stays gated on `data?.id` so editing other
  // fields doesn't blow away unsaved edits.
  useEffect(() => {
    if (data?.name) setName(data.name);
  }, [data?.name]);

  // Reset local state whenever a different request loads.
  useEffect(() => {
    if (!data) return;
    setName(data.name);
    setMethod(data.method);
    setUrl(data.url);
    setHeaders(data.headers.length ? data.headers : []);
    setQuery(data.query.length ? data.query : []);
    // Legacy "form" body type → "urlencoded". The two are functionally
    // identical (both send application/x-www-form-urlencoded) and we no
    // longer surface "form" in the picker. The next save migrates the
    // stored value over. `data.body_type` is typed as BodyType already
    // but the database can still hold "form" so the cast is intentional.
    const stored = data.body_type as string;
    setBodyType((stored === "form" ? "urlencoded" : stored) as BodyType);
    setBody(data.body ?? "");
    setDescription(data.description ?? "");
    setDocs(data.docs ?? "");
    setAuthKind((data.auth?.kind as AuthKind) ?? "none");
    setAuthFields(data.auth?.fields ?? {});
    setPreScript(data.pre_request_script ?? "");
    setPostScript(data.post_response_script ?? "");
    setTestsScript(data.tests_script ?? "");
    setSavedAt(data.updated_at);
    setResponse(null);
  }, [data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reflect the active request in the browser tab title so it's easy to
  // tell tabs apart in the OS switcher. Falls back to the base brand on
  // unmount so other routes don't inherit a stale name.
  useEffect(() => {
    if (!name) return;
    const prev = document.title;
    document.title = `${name} · API Stash`;
    return () => { document.title = prev; };
  }, [name]);

  const isDirty = useMemo(() => {
    if (!data) return false;
    const savedAuth = data.auth ?? { kind: "none" as AuthKind, fields: {} };
    return (
      data.name !== name ||
      data.method !== method ||
      data.url !== url ||
      JSON.stringify(data.headers) !== JSON.stringify(headers) ||
      JSON.stringify(data.query) !== JSON.stringify(query) ||
      ((data.body_type as string) === "form" ? "urlencoded" : data.body_type) !== bodyType ||
      (data.body ?? "") !== body ||
      (data.description ?? "") !== description ||
      (data.docs ?? "") !== docs ||
      (data.pre_request_script ?? "") !== preScript ||
      (data.post_response_script ?? "") !== postScript ||
      (data.tests_script ?? "") !== testsScript ||
      savedAuth.kind !== authKind ||
      JSON.stringify(savedAuth.fields) !== JSON.stringify(authFields)
    );
  }, [data, name, method, url, headers, query, bodyType, body, description, docs, preScript, postScript, testsScript, authKind, authFields]);

  const save = useMutation({
    mutationFn: (note?: string) => api.requests.update(requestId, {
      name, method, url, headers, query, body_type: bodyType, body, description,
      docs, pre_request_script: preScript || null, post_response_script: postScript || null, tests_script: testsScript || null,
      auth: { kind: authKind, fields: authFields },
      note,
    }),
    onSuccess: () => {
      setSavedAt(new Date().toISOString());
      qc.invalidateQueries({ queryKey: ["request", requestId] });
      qc.invalidateQueries({ queryKey: ["revisions", requestId] });
      if (data?.collection_id) qc.invalidateQueries({ queryKey: ["requests", data.collection_id] });
    },
  });

  const run = useMutation({
    mutationFn: async () => {
      // Save first if dirty so the agent runs the current scripts.
      if (isDirty) await save.mutateAsync(undefined);
      // Hold the loading state for at least 300ms so the user gets a
      // visible "sending" beat even on fast local responses. Real-world
      // requests usually take longer so this is rarely the bottleneck.
      const started = Date.now();
      const result = await api.requests.run(requestId, {
        overrides: { method, url, headers, query, body_type: bodyType, body },
        environmentId: envId || undefined,
        executor,
        agentId: executor === "agent" ? (agentId || undefined) : undefined,
      });
      const elapsed = Date.now() - started;
      if (elapsed < 300) await new Promise((r) => setTimeout(r, 300 - elapsed));
      return result;
    },
    onSuccess: (r) => {
      setResponse(r);
      qc.invalidateQueries({ queryKey: ["runs", requestId] });
      if (isDirty) {
        setSavedAt(new Date().toISOString());
        qc.invalidateQueries({ queryKey: ["request", requestId] });
        qc.invalidateQueries({ queryKey: ["revisions", requestId] });
        if (data?.collection_id) qc.invalidateQueries({ queryKey: ["requests", data.collection_id] });
      }
    },
  });

  const onlineAgents = (agentsQ.data?.agents ?? []).filter((a) => a.online);

  const remove = useMutation({
    mutationFn: () => api.requests.remove(requestId),
    onSuccess: () => {
      if (data?.collection_id) qc.invalidateQueries({ queryKey: ["requests", data.collection_id] });
      // Close the dead tab and jump to the next-active tab if any are
      // still open — otherwise home. Same rule the row-level delete
      // uses, so the two paths feel identical.
      // Tab IDs are namespaced (`r:` / `w:`) since webhooks share the
      // open-tab list. Use the namespaced form so the dead tab is found.
      const { search, nextActive } = closeOpenTabFor(workspaceId, `r:${requestId}`);
      const path = nextActive
        ? (nextActive.startsWith("w:") ? `/w/${nextActive.slice(2)}` : nextActive.startsWith("r:") ? `/r/${nextActive.slice(2)}` : `/r/${nextActive}`)
        : "/";
      window.location.assign(path + search);
    },
  });

  // Resizable split between the editor and the response panel. Declared
  // BEFORE the early returns below — hooks must run on every render.
  // The user can flip between horizontal (editor | response, default
  // on desktop) and vertical (editor / response, default on mobile).
  // Persisted per browser so it sticks across reloads.
  const isDesktopWidth = useMediaQuery("(min-width: 768px)");
  const [layoutMode, setLayoutMode] = useState<"horizontal" | "vertical">(() => {
    try {
      const v = localStorage.getItem("rest.builder.layoutMode");
      if (v === "horizontal" || v === "vertical") return v;
    } catch { /* ignore */ }
    return "horizontal";
  });
  // Mobile is too narrow for horizontal — always force vertical there.
  const splitDir: "horizontal" | "vertical" = isDesktopWidth ? layoutMode : "vertical";
  const persistLayout = (m: "horizontal" | "vertical") => {
    setLayoutMode(m);
    try { localStorage.setItem("rest.builder.layoutMode", m); } catch { /* ignore */ }
  };
  const { size: editorWidth, dragProps: editorDragX } = useResizable({
    axis: "x", initial: 540, min: 100, max: 1600, storageKey: "rest.builder.editorWidth",
  });
  const { size: editorHeight, dragProps: editorDragY } = useResizable({
    axis: "y", initial: 320, min: 100, max: 1200, storageKey: "rest.builder.editorHeight",
  });
  // Cap the editor pane so dragging can never crush the response pane
  // below 100px. The handle itself eats ~6px, so allow 106px of cushion
  // on the opposite axis. Min is enforced by useResizable's `min`.
  const editorPaneStyle = splitDir === "horizontal"
    ? { width: editorWidth, maxWidth: "calc(100% - 106px)" }
    : { height: editorHeight, maxHeight: "calc(100% - 106px)" };

  // Stable resolver + suggestion source for ChipInput — declared BEFORE
  // the early returns so hook order is consistent across renders.
  //
  // `allVars` is the UNION of variable names across every environment
  // in the workspace. We use this both for the autosuggest list (so
  // typing `H` shows HOST even when no env is selected) and the
  // "is this resolved" check (so the chip stays green if the name is
  // defined in *any* env, even when a different env is active).
  // The active env's value wins for the hint shown in the popover.
  const activeEnv = (envsQ.data?.environments ?? []).find((e) => e.id === envId);
  const allEnvKeySig = (envsQ.data?.environments ?? [])
    .flatMap((e) => Object.keys(e.vars ?? {}))
    .sort()
    .join("|") + "|active:" + (envId ?? "");
  const allVars = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const env of envsQ.data?.environments ?? []) {
      for (const [k, v] of Object.entries(env.vars ?? {})) {
        if (!(k in out)) out[k] = v;
      }
    }
    // Active env's value wins (if present) so the popover hint matches
    // what would actually substitute on Send.
    if (activeEnv) {
      for (const [k, v] of Object.entries(activeEnv.vars ?? {})) out[k] = v;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEnvKeySig]);
  // A chip is "resolved" strictly relative to the active env. If no
  // env is selected, NOTHING resolves — every chip is red until you
  // pick one. Otherwise it's resolved only if the active env defines
  // that name. Switch envs → chips flip color in place.
  const activeEnvKeySig = Object.keys(activeEnv?.vars ?? {}).sort().join("|");
  const isVarResolved = useMemo(() => {
    if (!activeEnv) return () => false;
    const keys = new Set(Object.keys(activeEnv.vars ?? {}));
    return (n: string) => keys.has(n);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEnv?.id, activeEnvKeySig]);
  // Tooltip value lookup — returns the value of a variable in the
  // active env (or undefined if not present). The chip uses this for
  // its `title` attr so hovering shows the substitution.
  const resolveVarValue = useMemo(() => {
    if (!activeEnv) return () => undefined;
    return (n: string) => activeEnv.vars?.[n];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEnv?.id, activeEnvKeySig]);

  const sendDisabled = !url || run.isPending;
  // Cmd/Ctrl+Enter sends the request from anywhere on the page — even
  // when the user is inside the URL or a body editor. The hotkey hook
  // already passes Mod+ bindings through input/textarea focus. Must be
  // called BEFORE the early returns below — hooks have to run in the
  // same order on every render.
  useHotkey("Mod+Enter", () => { if (!sendDisabled) run.mutate(); });

  if (detailQ.isLoading) {
    return <div className="grid h-full place-items-center"><InlineLoading label="Loading request…" /></div>;
  }
  if (!data) {
    return <div className="grid h-full place-items-center muted">Request not found.</div>;
  }

  // One-line explanation surfaced as a tooltip when the user hovers
  // the disabled Send button. Order matters — pending is the more
  // useful reason to show when both conditions apply.
  const sendDisabledReason: string | null =
    run.isPending ? "A request is already in flight — wait for it to finish."
    : !url ? "Enter a URL above before you can send."
    : null;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="border-b border-border bg-surface px-3 py-1.5 flex items-center gap-2">
        {/* Name lives in the sidebar + tab strip now — double-click either to
            rename. The static label keeps the header from feeling empty. */}
        <div className="flex-1 min-w-0 truncate text-sm font-medium text-text" title={name}>{name || "Untitled request"}</div>
        {isDirty && <span className="chip bg-warn/15 text-warn">Unsaved</span>}
        {!isDirty && savedAt && (
          <RelativeTime iso={savedAt} className="text-xs muted-strong" prefix="Saved " />
        )}
        {copyToast && <span className="chip bg-good/15 text-good">{copyToast}</span>}
        <LoadingButton
          className="btn"
          pending={save.isPending}
          disabled={!isDirty}
          onClick={() => save.mutate(undefined)}
        >
          Save
        </LoadingButton>
        <RequestMenu
          onDelete={async () => {
            const ok = await confirm({
              title: `Delete "${data.name}"?`,
              description: "This permanently removes the request and all its revisions and runs.",
              danger: true,
            });
            if (ok) remove.mutate();
          }}
        />
      </div>
      {codeOpen && (
        <CodeExport
          spec={{
            name,
            method: method as CodegenSpec["method"],
            // Substitute `{{var}}` references with the active env's
            // values so the exported snippet is runnable as-is.
            url: activeEnv ? interpolate(url, activeEnv.vars) : url,
            headers: headers.map((h) => ({
              key: activeEnv ? interpolate(h.key, activeEnv.vars) : h.key,
              value: activeEnv ? interpolate(h.value, activeEnv.vars) : h.value,
              enabled: h.enabled !== false,
            })),
            query: query.map((q) => ({
              key: activeEnv ? interpolate(q.key, activeEnv.vars) : q.key,
              value: activeEnv ? interpolate(q.value, activeEnv.vars) : q.value,
              enabled: q.enabled !== false,
            })),
            bodyType: bodyType as CodegenSpec["bodyType"],
            body: body ? (activeEnv ? interpolate(body, activeEnv.vars) : body) : null,
            auth: { kind: "none", fields: {} },
          }}
          onClose={() => setCodeOpen(false)}
        />
      )}

      <div className="px-3 py-3 flex flex-wrap sm:flex-nowrap items-stretch gap-2 border-b border-border">
        <MethodPicker value={method} onChange={setMethod} className="shrink-0" />
        <div className="min-w-0 relative flex items-center order-3 sm:order-none basis-full sm:basis-auto sm:flex-1">
          <ChipInput
            value={url}
            onChange={setUrl}
            suggestions={Object.keys(allVars).sort().map((n) => ({ name: n, hint: allVars[n] ?? "" }))}
            isResolved={isVarResolved}
            resolveValue={resolveVarValue}
            placeholder="Request URL — https://api.example.com/v1/users"
            spellCheck={false}
            aria-label="Request URL"
            className="!text-sm"
            onEnter={() => { if (!sendDisabled) run.mutate(); }}
          />
        </div>
        <SendButton
          className="ml-auto sm:ml-0"
          pending={run.isPending}
          disabled={sendDisabled}
          disabledReason={sendDisabledReason}
          onSend={() => run.mutate()}
          onCopyCurl={async () => {
            // Interpolate everything against the active env so the
            // copied curl is runnable as-is, not a template.
            const interp = (s: string) => activeEnv ? interpolate(s, activeEnv.vars) : s;
            const spec: CurlSpec = {
              name,
              method: method as CurlSpec["method"],
              url: interp(url),
              headers: headers.map((h) => ({ key: interp(h.key), value: interp(h.value), enabled: h.enabled !== false })),
              query:   query.map((q) => ({ key: interp(q.key), value: interp(q.value), enabled: q.enabled !== false })),
              bodyType: bodyType as CurlSpec["bodyType"],
              body: body ? interp(body) : null,
              auth: { kind: "none", fields: {} },
              followRedirects: true, insecure: false, unsupported: [],
            };
            // Single line: multiline output with backslash continuations
            // gets mangled by paste targets that collapse newlines, which
            // leaves literal "\ " escaped-space args mid-command.
            const out = emitCurl(spec, { multiline: false });
            copyToClipboard(out, "Copied as curl");
          }}
          onShowCode={() => setCodeOpen(true)}
        />
      </div>

      <div className="border-b border-border bg-surface px-3 flex items-center gap-2 overflow-x-auto">
        <Tabs current={tab} onChange={setTab} counts={{
          params: query.filter((q) => q.key).length,
          headers: headers.filter((h) => h.key).length,
          docs: docs ? 1 : 0,
          authActive: authKind !== "none",
        }} />
        {/* Layout toggle sits on the right side of the tab strip on
            desktop only. Hidden on mobile since vertical is the only
            sensible split there. */}
        {isDesktopWidth && (
          <div className="ml-auto hidden md:flex shrink-0">
            <LayoutToggle mode={layoutMode} onChange={persistLayout} />
          </div>
        )}
      </div>

      <div className={"flex-1 min-h-0 flex " + (splitDir === "horizontal" ? "flex-row" : "flex-col")}>
        {/* The docs tab is full-width — long Markdown is uncomfortable
            to read in a half-width column. Comments and history keep
            the split so you can reference the last response. */}
        {(() => {
          const fullWidth = tab === "docs";
          // Border + size axis flip with splitDir. Horizontal: right
          // border + fixed width on editor; vertical: bottom border +
          // fixed height. Tailwind doesn't have a "border-along-axis"
          // helper so we concat the right class explicitly.
          const sideClasses = splitDir === "horizontal" ? "border-r" : "border-b";
          return (
            <>
              <div
                className={
                  "min-h-0 overflow-auto scroll-pane p-3 border-border " +
                  sideClasses + " " +
                  (fullWidth ? "flex-1 min-w-0" : "shrink-0")
                }
                style={fullWidth ? undefined : editorPaneStyle}
              >
                {tab === "params" && (
                  <KVEditor
                    items={query}
                    onChange={setQuery}
                    keyLabel="Param"
                    valueLabel="Value"
                    vars={allVars}
                    envVarNames={Object.keys(allVars).sort()}
                    isResolved={isVarResolved}
                    resolveValue={resolveVarValue}
                  />
                )}
                {tab === "headers" && (
                  <KVEditor
                    items={headers}
                    onChange={setHeaders}
                    keyLabel="Header"
                    valueLabel="Value"
                    vars={allVars}
                    envVarNames={Object.keys(allVars).sort()}
                    isResolved={isVarResolved}
                    resolveValue={resolveVarValue}
                  />
                )}
                {tab === "body" && (
                  <BodyEditor
                    bodyType={bodyType}
                    body={body}
                    onTypeChange={setBodyType}
                    onBodyChange={setBody}
                    vars={allVars}
                    envVarNames={Object.keys(allVars).sort()}
                    isResolved={isVarResolved}
                    resolveValue={resolveVarValue}
                    requestId={requestId}
                  />
                )}
                {tab === "auth" && (
                  <AuthEditor
                    kind={authKind}
                    fields={authFields}
                    onChange={(next) => { setAuthKind(next.kind); setAuthFields(next.fields); }}
                    requestId={requestId}
                    dirty={isDirty}
                  />
                )}
                {tab === "docs" && (
                  <DocsEditor description={description} setDescription={setDescription} docs={docs} setDocs={setDocs} />
                )}
                {tab === "comments" && me && (
                  <Comments targetKind="request" targetId={requestId} me={me} workspaceId={workspaceId} />
                )}
                {tab === "revisions" && (
                  <RevisionsPane
                    requestId={requestId}
                    onRestore={() => {
                      qc.invalidateQueries({ queryKey: ["request", requestId] });
                      qc.invalidateQueries({ queryKey: ["revisions", requestId] });
                    }}
                  />
                )}
                {tab === "runs" && (
                  <RunsPane requestId={requestId} />
                )}
              </div>
              {!fullWidth && (
                <>
                  {/* Drag handle. Only one is mounted at a time — its
                      axis follows `splitDir`. docs/comments/history make
                      the editor full-width and drop both the handle and
                      the response pane. */}
                  {splitDir === "horizontal" ? (
                    <div
                      {...editorDragX}
                      aria-label="Resize editor / response"
                      className="w-1.5 cursor-col-resize bg-transparent hover:bg-accent active:bg-accent-bright transition-colors shrink-0"
                    />
                  ) : (
                    <div
                      {...editorDragY}
                      aria-label="Resize editor / response"
                      className="h-1.5 cursor-row-resize bg-transparent hover:bg-accent active:bg-accent-bright transition-colors shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-[100px] min-h-[100px] overflow-auto scroll-pane">
                    <ResponseViewer
                      response={response}
                      error={run.error ? String((run.error as Error).message) : null}
                      pending={run.isPending}
                      requestId={requestId}
                      workspaceId={workspaceId}
                    />
                  </div>
                </>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}

// Two-state toggle for the editor/response split direction. The icons
// are a stylized side-by-side or stacked rectangle so the active state
// is glanceable at small sizes.
function LayoutToggle({ mode, onChange }: { mode: "horizontal" | "vertical"; onChange: (m: "horizontal" | "vertical") => void }) {
  return (
    <div role="group" aria-label="Layout" className="inline-flex rounded-md border border-border overflow-hidden">
      <button
        type="button"
        aria-label="Side-by-side layout"
        title="Side-by-side layout"
        onClick={() => onChange("horizontal")}
        aria-pressed={mode === "horizontal"}
        className={
          "h-7 w-8 grid place-items-center " +
          (mode === "horizontal" ? "bg-surface-2 text-text" : "text-text-faint hover:text-text hover:bg-surface-2")
        }
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="5" width="8" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
          <rect x="13" y="5" width="8" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      </button>
      <button
        type="button"
        aria-label="Stacked layout"
        title="Stacked layout"
        onClick={() => onChange("vertical")}
        aria-pressed={mode === "vertical"}
        className={
          "h-7 w-8 grid place-items-center border-l border-border " +
          (mode === "vertical" ? "bg-surface-2 text-text" : "text-text-faint hover:text-text hover:bg-surface-2")
        }
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="4" width="18" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
          <rect x="3" y="13" width="18" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      </button>
    </div>
  );
}

function Tabs({ current, onChange, counts }: { current: Tab; onChange: (t: Tab) => void; counts: { params: number; headers: number; docs: number; authActive: boolean } }) {
  const items: Array<{ id: Tab; label: string; count?: number; dot?: boolean }> = [
    { id: "params", label: "Params", count: counts.params },
    { id: "headers", label: "Headers", count: counts.headers },
    { id: "body", label: "Body" },
    { id: "auth", label: "Auth", dot: counts.authActive },
    { id: "docs", label: "Docs", count: counts.docs },
    { id: "comments", label: "Comments" },
    { id: "revisions", label: "Revisions" },
    { id: "runs", label: "Runs" },
  ];
  return (
    <div className="flex gap-1">
      {items.map((t) => {
        const active = t.id === current;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={
              "px-3 py-2 text-sm font-medium border-b-2 -mb-px " +
              (active ? "border-accent text-text" : "border-transparent text-text-dim hover:text-text")
            }
          >
            {t.label}
            {!!t.count && (
              <span className="ml-1.5 chip bg-surface-3 muted-strong">{t.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function KVEditor({
  items,
  onChange,
  keyLabel,
  valueLabel,
  vars,
  envVarNames,
  isResolved,
  resolveValue,
}: {
  items: KV[];
  onChange: (v: KV[]) => void;
  keyLabel: string;
  valueLabel: string;
  vars: Record<string, string>;
  envVarNames: string[];
  isResolved: (name: string) => boolean;
  resolveValue?: (name: string) => string | undefined;
}) {
  const list = items;
  // Stable row IDs so dnd-kit can identify rows across re-renders. We
  // mint one per row index lazily — the underlying KV[] has no id, so
  // we just use the row position.
  const ids = useMemo(() => list.map((_, i) => `kv-${i}`), [list]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const set = (i: number, patch: Partial<KV>) => {
    onChange(list.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  };
  const removeRow = (i: number) => onChange(list.filter((_, idx) => idx !== i));
  const addRow = () => onChange([...list, { key: "", value: "", enabled: true }]);

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onChange(arrayMove(list, from, to));
  };

  return (
    <div className="space-y-1 min-w-0">
      {list.length > 0 && (
        <div className="grid grid-cols-[18px_28px_minmax(0,1fr)_minmax(0,1fr)_28px] gap-2 px-2 text-[11px] uppercase tracking-wider muted-strong">
          <div></div>
          <div></div>
          <div>{keyLabel}</div>
          <div>{valueLabel}</div>
          <div></div>
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictKVtoVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {list.map((it, i) => (
            <KVRow
              key={ids[i]}
              id={ids[i]}
              kv={it}
              keyLabel={keyLabel}
              valueLabel={valueLabel}
              vars={vars}
              envVarNames={envVarNames}
              isResolved={isResolved}
              resolveValue={resolveValue}
              onPatch={(p) => set(i, p)}
              onRemove={() => removeRow(i)}
            />
          ))}
        </SortableContext>
      </DndContext>
      {list.length === 0 && (
        <div className="empty-card">No {valueLabel.toLowerCase()}s yet. Click <span className="empty-card-strong">+ Add</span> to add one.</div>
      )}
      <div className="pt-1">
        <button type="button" className="btn" onClick={addRow}>＋ Add {keyLabel.toLowerCase()}</button>
      </div>
    </div>
  );
}

function KVRow({
  id, kv, keyLabel, valueLabel, vars, envVarNames, isResolved, resolveValue, onPatch, onRemove,
}: {
  id: string;
  kv: KV;
  keyLabel: string;
  valueLabel: string;
  vars: Record<string, string>;
  envVarNames: string[];
  isResolved: (name: string) => boolean;
  resolveValue?: (name: string) => string | undefined;
  onPatch: (p: Partial<KV>) => void;
  onRemove: () => void;
}) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.6 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} className="grid grid-cols-[18px_28px_minmax(0,1fr)_minmax(0,1fr)_28px] gap-2 items-center">
      <button
        type="button"
        {...attributes}
        {...listeners}
        title="Drag to reorder"
        aria-label="Drag to reorder"
        className="h-7 w-[18px] grid place-items-center text-text-faint hover:text-text cursor-grab active:cursor-grabbing select-none"
      >
        <DragHandleIcon />
      </button>
      <input
        type="checkbox"
        checked={kv.enabled !== false}
        onChange={(e) => onPatch({ enabled: e.target.checked })}
        className="h-4 w-4 rounded justify-self-center"
      />
      <ChipInput
        value={kv.key}
        onChange={(v) => onPatch({ key: v })}
        suggestions={envVarNames.map((n) => ({ name: n, hint: vars[n] ?? "" }))}
        isResolved={isResolved}
        resolveValue={resolveValue}
        placeholder={keyLabel}
        spellCheck={false}
      />
      <ChipInput
        value={kv.value}
        onChange={(v) => onPatch({ value: v })}
        suggestions={envVarNames.map((n) => ({ name: n, hint: vars[n] ?? "" }))}
        isResolved={isResolved}
        resolveValue={resolveValue}
        placeholder={valueLabel}
        spellCheck={false}
      />
      <button
        type="button"
        onClick={onRemove}
        className="h-7 w-7 grid place-items-center rounded text-text-faint hover:text-danger hover:bg-surface-2 justify-self-center"
        title="Remove"
        aria-label="Remove"
      >
        ✕
      </button>
    </div>
  );
}

function DragHandleIcon() {
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" aria-hidden="true">
      <g fill="currentColor">
        <circle cx="2" cy="2" r="1.2" />
        <circle cx="8" cy="2" r="1.2" />
        <circle cx="2" cy="7" r="1.2" />
        <circle cx="8" cy="7" r="1.2" />
        <circle cx="2" cy="12" r="1.2" />
        <circle cx="8" cy="12" r="1.2" />
      </g>
    </svg>
  );
}

// dnd-kit modifier that constrains the dragged item to the vertical axis
// so the row can only shuffle up/down (no horizontal drift).
function restrictKVtoVerticalAxis({ transform }: { transform: { x: number; y: number; scaleX: number; scaleY: number } }) {
  return { ...transform, x: 0 };
}

function BodyEditor({
  bodyType,
  body,
  onTypeChange,
  onBodyChange,
  vars,
  envVarNames,
  isResolved,
  resolveValue,
  requestId,
}: {
  bodyType: BodyType;
  body: string;
  onTypeChange: (t: BodyType) => void;
  onBodyChange: (s: string) => void;
  vars: Record<string, string>;
  envVarNames: string[];
  isResolved: (name: string) => boolean;
  resolveValue?: (name: string) => string | undefined;
  requestId: string;
}) {
  // Picker options. Order matters — the dropdown reads top-to-bottom so
  // we lead with the things users want most.
  const options: Array<{ value: BodyType; label: string }> = [
    { value: "none",       label: "None" },
    { value: "json",       label: "JSON" },
    { value: "text",       label: "Text" },
    { value: "xml",        label: "XML" },
    { value: "urlencoded", label: "Form URL Encoded" },
    { value: "multipart",  label: "Multipart Form" },
    { value: "graphql",    label: "GraphQL" },
  ];

  // Validate JSON on each change; show a precise error pill when invalid.
  // The native JSON.parse error message includes the byte offset for V8
  // ("at position 42"), so we surface it as-is — useful for fixing typos.
  const jsonError = useMemo(() => {
    if (bodyType !== "json" || !body.trim()) return null;
    try { JSON.parse(body); return null; }
    catch (e: unknown) { return String((e as Error).message ?? e); }
  }, [body, bodyType]);

  return (
    <div className="space-y-2 h-full flex flex-col min-w-0">
      <div className="flex items-center gap-3 flex-wrap min-w-0">
        <label className="flex items-center gap-2 text-xs muted-strong">
          <span className="label">Type</span>
          <div className="relative">
            <select
              value={bodyType}
              onChange={(e) => onTypeChange(e.target.value as BodyType)}
              // `appearance: none` on every flavor so Safari/Firefox
              // also drop the native chevron — Tailwind's appearance-none
              // misses Safari's, which is how the page ended up with two
              // arrows.
              style={{ appearance: "none", WebkitAppearance: "none", MozAppearance: "none" }}
              className="input pr-9 py-1 text-sm cursor-pointer min-w-[200px]"
            >
              {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <svg
              aria-hidden="true"
              width="12" height="12" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
              className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-text-faint"
            ><path d="M6 9l6 6 6-6"/></svg>
          </div>
        </label>
        <span className="text-[11px] muted-strong">{hintFor(bodyType)}</span>
      </div>
      {bodyType === "json" && jsonError && (
        <div className="rounded-md border border-danger/20 bg-danger/[0.06] px-2 py-1 text-[11px] text-danger/80 flex items-center gap-2">
          <span aria-hidden="true" className="opacity-70">⚠</span>
          <span className="flex-1 truncate" title={jsonError}>Invalid JSON · {jsonError}</span>
        </div>
      )}
      {bodyType === "none" ? (
        <div className="empty-card">
          No body sent with this request. Pick <span className="empty-card-strong">JSON</span>, <span className="empty-card-strong">Text</span>, <span className="empty-card-strong">XML</span>, <span className="empty-card-strong">Form URL Encoded</span>, or <span className="empty-card-strong">Multipart Form</span> in the dropdown above to add one.
        </div>
      ) : bodyType === "json" || bodyType === "xml" ? (
        <div className="relative flex-1 min-h-[160px]">
          <JsonCodeEditor
            value={body}
            onChange={onBodyChange}
            lang={bodyType}
            isResolved={isResolved}
            resolveValue={resolveValue}
            envVarNames={envVarNames}
            vars={vars}
          />
          {bodyType === "json" && (
            <button
              type="button"
              disabled={!!jsonError || !body.trim()}
              onClick={() => {
                try { onBodyChange(JSON.stringify(JSON.parse(body), null, 2)); } catch { /* leave alone */ }
              }}
              className="absolute bottom-2 right-3 z-10 h-6 px-2 rounded bg-surface-2/90 border border-border text-[11px] font-medium text-text-dim hover:text-text hover:bg-surface-3 backdrop-blur-sm disabled:opacity-40 disabled:cursor-not-allowed transition"
            >Format</button>
          )}
        </div>
      ) : bodyType === "graphql" ? (
        <GraphQLBodyEditor
          body={body}
          onBodyChange={onBodyChange}
        />
      ) : bodyType === "multipart" ? (
        <MultipartBodyEditor
          body={body}
          onBodyChange={onBodyChange}
          requestId={requestId}
          vars={vars}
          envVarNames={envVarNames}
          isResolved={isResolved}
          resolveValue={resolveValue}
        />
      ) : bodyType === "urlencoded" ? (
        <FormBodyEditor
          body={body}
          onBodyChange={onBodyChange}
          vars={vars}
          envVarNames={envVarNames}
          isResolved={isResolved}
          resolveValue={resolveValue}
        />
      ) : (
        // Plain text body. ChipInput in var mode renders {{NAME}} as
        // chips, exactly like the URL bar — same suggest popover, same
        // resolved/unresolved coloring against the active env.
        <div className="relative flex-1 min-h-[160px] flex flex-col">
          <ChipInput
            kind="var"
            value={body}
            onChange={onBodyChange}
            multiline
            rows={10}
            suggestions={envVarNames.map((n) => ({ name: n, hint: vars[n] ?? "" }))}
            isResolved={isResolved}
            resolveValue={resolveValue}
            placeholder={placeholderFor(bodyType)}
            spellCheck={false}
          />
        </div>
      )}
    </div>
  );
}

function placeholderFor(t: BodyType): string {
  if (t === "xml") return "<root>\n  <item>{{VAR}}</item>\n</root>";
  if (t === "text") return "Plain text body. Use {{VAR}} for env substitution.";
  return "";
}
function hintFor(t: BodyType): string {
  if (t === "json")       return "Sent as application/json. Use {{VAR}} anywhere to inject env values.";
  if (t === "xml")        return "Sent as application/xml.";
  if (t === "text")       return "Sent as text/plain.";
  if (t === "urlencoded") return "Sent as application/x-www-form-urlencoded. Edit fields below.";
  if (t === "multipart")  return "Sent as multipart/form-data with a generated boundary.";
  if (t === "graphql")    return "Sent as application/json: { query, variables, operationName }.";
  return "";
}

// KV editor for url-encoded / multipart bodies. We serialize to a plain
// `key=value\nkey=value` string that the server already knows how to
// parse for both content types — keeping the body column as a TEXT in
// the database while still giving users a structured editor with chip
// suggestions on each value.
type FormKV = { key: string; value: string; enabled?: boolean };

function parseFormBody(s: string): FormKV[] {
  if (!s || !s.trim()) return [];
  // Accept both `\n` and `&` separators so existing legacy bodies still
  // load correctly. We DON'T decodeURIComponent values — the user may
  // have entered them as plain text or as `{{VAR}}` tokens, and decoding
  // would corrupt those.
  const lines = s.split(/[&\n]/).map((l) => l.trim()).filter(Boolean);
  const out: FormKV[] = [];
  for (const line of lines) {
    const eq = line.indexOf("=");
    if (eq < 0) out.push({ key: line, value: "", enabled: true });
    else out.push({ key: line.slice(0, eq), value: line.slice(eq + 1), enabled: true });
  }
  return out;
}

function serializeFormBody(rows: FormKV[]): string {
  return rows
    .filter((r) => (r.enabled !== false) && (r.key || r.value))
    .map((r) => `${r.key}=${r.value}`)
    .join("\n");
}

function FormBodyEditor({
  body, onBodyChange, vars, envVarNames, isResolved, resolveValue,
}: {
  body: string;
  onBodyChange: (s: string) => void;
  vars: Record<string, string>;
  envVarNames: string[];
  isResolved: (name: string) => boolean;
  resolveValue?: (name: string) => string | undefined;
}) {
  const rows = useMemo<FormKV[]>(() => parseFormBody(body), [body]);

  const setRows = (next: FormKV[]) => onBodyChange(serializeFormBody(next));
  const set = (i: number, patch: Partial<FormKV>) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i: number) => setRows(rows.filter((_, idx) => idx !== i));
  const addRow = () => setRows([...rows, { key: "", value: "", enabled: true }]);

  return (
    <div className="space-y-1 min-w-0">
      {rows.length > 0 && (
        <div className="grid grid-cols-[28px_minmax(0,1fr)_minmax(0,1fr)_28px] gap-2 px-2 text-[11px] uppercase tracking-wider muted-strong">
          <div></div>
          <div>Field</div>
          <div>Value</div>
          <div></div>
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[28px_minmax(0,1fr)_minmax(0,1fr)_28px] gap-2 items-center">
          <input
            type="checkbox"
            checked={r.enabled !== false}
            onChange={(e) => set(i, { enabled: e.target.checked })}
            className="h-4 w-4 rounded justify-self-center"
          />
          <ChipInput
            value={r.key}
            onChange={(v) => set(i, { key: v })}
            suggestions={envVarNames.map((n) => ({ name: n, hint: vars[n] ?? "" }))}
            isResolved={isResolved}
            resolveValue={resolveValue}
            placeholder="Field"
            spellCheck={false}
          />
          <ChipInput
            value={r.value}
            onChange={(v) => set(i, { value: v })}
            suggestions={envVarNames.map((n) => ({ name: n, hint: vars[n] ?? "" }))}
            isResolved={isResolved}
            resolveValue={resolveValue}
            placeholder="Value"
            spellCheck={false}
          />
          <button
            type="button"
            onClick={() => removeRow(i)}
            className="h-7 w-7 grid place-items-center rounded text-text-faint hover:text-danger hover:bg-surface-2 justify-self-center"
            title="Remove"
            aria-label="Remove"
          >✕</button>
        </div>
      ))}
      {rows.length === 0 && (
        <div className="empty-card">No fields yet. Click <span className="empty-card-strong">+ Add field</span> to add one.</div>
      )}
      <div className="pt-1">
        <button type="button" className="btn" onClick={addRow}>＋ Add field</button>
      </div>
    </div>
  );
}

function ScriptEditor({
  title, hint, code, onChange, example,
}: { title: string; hint: string; code: string; onChange: (s: string) => void; example: string }) {
  return (
    <div className="h-full flex flex-col space-y-2">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="text-xs muted mt-1">{hint}</p>
      </div>
      <textarea
        className="input flex-1 input-mono min-h-[200px]"
        value={code}
        onChange={(e) => onChange(e.target.value)}
        placeholder={example}
        spellCheck={false}
      />
      <p className="text-[11px] muted-strong">
        Scripts run on the agent. Connect one from <a href="/agents" className="accent-link">Agents</a> and switch the
        executor to "Local agent" above.
      </p>
    </div>
  );
}

function DocsEditor({ description, setDescription, docs, setDocs }: { description: string; setDescription: (s: string) => void; docs: string; setDocs: (s: string) => void }) {
  const [mode, setMode] = useState<"edit" | "preview" | "split">("split");
  return (
    <div className="space-y-3 h-full flex flex-col min-w-0">
      <div className="min-w-0">
        <label className="label">Short description</label>
        <input className="input mt-1" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="One line — shows up in lists." />
      </div>
      <div className="flex items-center gap-2 flex-wrap min-w-0">
        <label className="label">Full docs (Markdown)</label>
        <div className="ml-auto flex gap-1">
          {(["edit", "split", "preview"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={
                "px-2 py-0.5 rounded-sm text-xs font-medium " +
                (m === mode ? "bg-accent text-[var(--ink-on-accent)]" : "bg-surface-2 text-text-dim hover:bg-surface-3 hover:text-text")
              }
            >{m}</button>
          ))}
        </div>
      </div>
      <div className={"flex-1 min-h-[240px] min-w-0 grid gap-3 " + (mode === "split" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1")}>
        {mode !== "preview" && (
          <textarea
            className="input input-mono min-h-[240px] min-w-0 w-full"
            value={docs}
            onChange={(e) => setDocs(e.target.value)}
            placeholder={`## What this endpoint does\n\n- bullet\n- bullet\n\n### Response shape\n\n\`\`\`json\n{ "id": 123 }\n\`\`\``}
            spellCheck={false}
          />
        )}
        {mode !== "edit" && (
          <div className="rounded border border-border bg-surface-2 p-3 overflow-auto scroll-pane min-h-[240px] min-w-0">
            {docs ? <Markdown source={docs} /> : <p className="text-sm muted italic">Nothing to preview yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// Two sibling panes: Revisions (every save) and Runs (every execution).
// They used to share a single "History" tab; splitting makes the
// scrolled list shorter and removes a confusing context switch when
// you're inspecting one but expecting the other.

function RevisionsPane({ requestId, onRestore }: { requestId: string; onRestore: () => void }) {
  // Clicking a row opens a focused modal (mirrors how Runs work) with the
  // full snapshot + diff + a Restore button. The modal is portaled, so
  // it isn't clipped by the tab pane's overflow.
  const [openId, setOpenId] = useState<string | null>(null);
  const revs = useQuery({
    queryKey: ["revisions", requestId],
    queryFn: () => api.requests.revisions(requestId),
  });
  const revisions = revs.data?.revisions ?? [];
  const openIdx = openId ? revisions.findIndex((r) => r.id === openId) : -1;
  const current = openIdx >= 0 ? revisions[openIdx] : null;
  // Revisions come back newest-first, so the previous version is the
  // next entry in the array.
  const prev = openIdx >= 0 ? revisions[openIdx + 1] ?? null : null;
  return (
    <div className="min-w-0">
      <h3 className="text-sm font-semibold mb-2">Revisions</h3>
      <p className="text-xs muted mb-3">Every save creates a revision. Click one to open the full snapshot, see what changed, and restore — restoring also creates a new revision so nothing is lost.</p>
      <ul className="space-y-1">
        {revisions.map((r) => {
          const avatarSrc = r.author_id && r.author_avatar_updated_at
            ? api.avatars.userUrl(r.author_id, r.author_avatar_updated_at)
            : null;
          const author = r.author_name ?? r.author_email ?? "unknown";
          return (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => setOpenId(r.id)}
                className="w-full flex items-start gap-3 px-2 py-2 rounded hover:bg-surface-2 text-left min-w-0"
                title="Open revision details"
              >
                <span className="chip bg-surface-3 muted-strong mono shrink-0 mt-0.5">#{r.revision_no}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="text-sm font-medium text-text truncate flex-1 min-w-0">
                      {r.change_summary?.trim() || r.note?.trim() || "Saved"}
                    </span>
                    <RelativeTime iso={r.created_at} className="text-xs muted-strong shrink-0" />
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-[11px] muted-strong min-w-0">
                    <span className="shrink-0"><Avatar src={avatarSrc} name={r.author_name} email={r.author_email ?? "unknown@unknown"} size={14} /></span>
                    <span className="truncate">{author}</span>
                    {r.note && r.change_summary && r.note !== r.change_summary && (
                      <>
                        <span className="muted-strong">·</span>
                        <span className="truncate italic">{r.note}</span>
                      </>
                    )}
                    <span className="muted-strong">·</span>
                    <span className={"method-tag shrink-0 " + r.method.toLowerCase()}>{r.method}</span>
                  </div>
                </div>
              </button>
            </li>
          );
        })}
        {revs.data && revisions.length === 0 && (
          <li className="empty-card">No revisions yet. Hit <span className="empty-card-strong">Save</span> to capture the first one.</li>
        )}
      </ul>
      <RevisionDetailModal
        requestId={requestId}
        current={current ? { id: current.id, revision_no: current.revision_no } : null}
        prev={prev ? { id: prev.id, revision_no: prev.revision_no } : null}
        onClose={() => setOpenId(null)}
        onRestored={onRestore}
      />
    </div>
  );
}

function RunsPane({ requestId }: { requestId: string }) {
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const runs = useQuery({
    queryKey: ["runs", requestId],
    queryFn: () => api.requests.runs(requestId),
  });
  return (
    <div className="min-w-0">
      <h3 className="text-sm font-semibold mb-2">Runs</h3>
      <p className="text-xs muted mb-3">Last 100 executions of this request.</p>
      <ul className="space-y-1">
        {(runs.data?.runs ?? []).map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => setOpenRunId(r.id)}
              className="w-full flex items-center gap-2 sm:gap-3 px-2 py-1.5 rounded hover:bg-surface-2 text-left min-w-0"
              title="Open run details"
            >
              <span className="shrink-0"><StatusPill status={r.status} error={r.error} /></span>
              <span className={"method-tag shrink-0 " + r.method.toLowerCase()}>{r.method}</span>
              <div className="flex-1 min-w-0">
                <VarText value={r.url} className="text-xs truncate font-mono block" />
                <div className="text-[11px] muted-strong truncate">
                  {new Date(r.ran_at).toLocaleString()} · {r.duration_ms} ms
                  {r.response_size != null && ` · ${formatBytes(r.response_size)}`}
                </div>
              </div>
            </button>
          </li>
        ))}
        {runs.data && runs.data.runs.length === 0 && (
          <li className="empty-card">No runs yet. Press <span className="empty-card-strong">Send</span> to execute this request.</li>
        )}
      </ul>
      <RunDetailModal requestId={requestId} runId={openRunId} onClose={() => setOpenRunId(null)} />
    </div>
  );
}

// Plain-English summary of what changed between two revisions. We
// fetch both revision payloads and walk the fields, then render one
// muted line per change with `old → new` for edits, `+` for adds, and
// `−` for removes. No side-by-side panels, no green/red walls of
// color — just a scannable list that mirrors a git short log.
function RevisionDiff({ requestId, fromId, toId, fromNo, toNo }: {
  requestId: string;
  fromId: string;
  toId: string;
  fromNo: number;
  toNo: number;
}) {
  const fromQ = useQuery({
    queryKey: ["revision", requestId, fromId],
    queryFn: () => api.requests.revision(requestId, fromId),
  });
  const toQ = useQuery({
    queryKey: ["revision", requestId, toId],
    queryFn: () => api.requests.revision(requestId, toId),
  });
  const [showBody, setShowBody] = useState(false);

  if (fromQ.isLoading || toQ.isLoading) {
    return <div className="text-xs muted"><InlineLoading label="Loading diff…" /></div>;
  }
  const a = fromQ.data?.revision;
  const b = toQ.data?.revision;
  if (!a || !b) {
    return <p className="text-xs muted italic">Couldn't load one or both revisions.</p>;
  }

  // Scalar field changes. Keep names short — the line itself shows
  // `old → new` so we don't need a column for the field name.
  const scalars: Array<{ field: string; from: string; to: string }> = [
    { field: "name",        from: a.name ?? "",        to: b.name ?? "" },
    { field: "method",      from: a.method,            to: b.method },
    { field: "URL",         from: a.url ?? "",         to: b.url ?? "" },
    { field: "body type",   from: a.body_type,         to: b.body_type },
    { field: "description", from: a.description ?? "", to: b.description ?? "" },
  ].filter((s) => s.from !== s.to);

  const headerChanges = diffKVList(a.headers ?? [], b.headers ?? []);
  const queryChanges = diffKVList(a.query ?? [], b.query ?? []);
  const bodyChanged = (a.body ?? "") !== (b.body ?? "");
  const bodyDelta = bodyLengthDelta(a.body, b.body);
  const anyChange = scalars.length > 0 || headerChanges.length > 0 || queryChanges.length > 0 || bodyChanged;

  return (
    <div className="space-y-2 min-w-0">
      <div className="text-[11px] muted-strong">
        Changes from <span className="mono">#{fromNo}</span> to <span className="mono">#{toNo}</span>
      </div>
      {!anyChange && <p className="text-xs muted italic">No differences.</p>}

      {scalars.length > 0 && (
        <ul className="space-y-0.5 text-xs">
          {scalars.map((s) => (
            <li key={s.field} className="flex items-baseline gap-2 min-w-0">
              <span className="muted-strong shrink-0 capitalize">{s.field}</span>
              <ChangeText from={s.from} to={s.to} />
            </li>
          ))}
        </ul>
      )}

      {headerChanges.length > 0 && (
        <KVChangeList label="Headers" changes={headerChanges} />
      )}
      {queryChanges.length > 0 && (
        <KVChangeList label="Query" changes={queryChanges} />
      )}

      {bodyChanged && (
        <div className="text-xs">
          <button
            type="button"
            onClick={() => setShowBody((v) => !v)}
            className="muted-strong hover:text-text inline-flex items-center gap-1"
          >
            <span className="text-text-faint">{showBody ? "▾" : "▸"}</span>
            <span>Body changed</span>
            <span className="muted">{bodyDelta}</span>
          </button>
          {showBody && (
            <pre className="mt-1 mono text-[11px] bg-surface-2 border border-border rounded p-2 max-h-48 overflow-auto whitespace-pre-wrap break-all">
              {renderBodyDiff(a.body ?? "", b.body ?? "")}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// `old → new` rendered in a single line. Empty values become a muted
// `∅` placeholder so the arrow still parses cleanly.
function ChangeText({ from, to }: { from: string; to: string }) {
  return (
    <span className="mono text-[11px] flex-1 min-w-0 break-all">
      <span className="muted line-through">{from || "∅"}</span>
      <span className="muted-strong mx-1.5">→</span>
      <span className="text-text">{to || "∅"}</span>
    </span>
  );
}

function bodyLengthDelta(a: string | null, b: string | null): string {
  const x = (a ?? "").length;
  const y = (b ?? "").length;
  if (x === y) return "";
  const sign = y > x ? "+" : "";
  return ` (${sign}${y - x} chars)`;
}

// Plain `before / after` body dump. Each pre tag gets a 3-line cap
// before scrolling so the inline render stays manageable. Keeping
// this simple is the whole point of the new diff design.
function renderBodyDiff(before: string, after: string): React.ReactNode {
  return (
    <>
      <span className="muted line-through">{before || "(empty)"}</span>
      {"\n\n"}
      <span className="text-text">{after || "(empty)"}</span>
    </>
  );
}

// Per-line diff for headers/query lists. We pair entries by index; if
// either side has an entry at index i and they differ, that counts as
// changed (or added/removed if only one side has it).
type KVChange =
  | { kind: "added"; after: KV }
  | { kind: "removed"; before: KV }
  | { kind: "changed"; before: KV; after: KV };

function diffKVList(a: KV[], b: KV[]): KVChange[] {
  const out: KVChange[] = [];
  const max = Math.max(a.length, b.length);
  for (let i = 0; i < max; i++) {
    const x = a[i];
    const y = b[i];
    if (x && y) {
      if (x.key !== y.key || x.value !== y.value || (x.enabled !== false) !== (y.enabled !== false)) {
        out.push({ kind: "changed", before: x, after: y });
      }
    } else if (y) {
      out.push({ kind: "added", after: y });
    } else if (x) {
      out.push({ kind: "removed", before: x });
    }
  }
  return out;
}

function KVChangeList({ label, changes }: { label: string; changes: KVChange[] }) {
  return (
    <div className="text-xs min-w-0">
      <div className="muted-strong mb-0.5">{label}</div>
      <ul className="space-y-0.5">
        {changes.map((c, i) => (
          <li key={i} className="flex items-baseline gap-2 min-w-0 mono text-[11px]">
            {c.kind === "added" && (
              <>
                <span className="text-good shrink-0">+</span>
                <span className="break-all"><span className="muted-strong">{c.after.key}</span>: <span className="text-text">{c.after.value}</span></span>
              </>
            )}
            {c.kind === "removed" && (
              <>
                <span className="text-danger shrink-0">−</span>
                <span className="break-all line-through muted">{c.before.key}: {c.before.value}</span>
              </>
            )}
            {c.kind === "changed" && (
              <>
                <span className="muted-strong shrink-0">~</span>
                <span className="break-all flex-1 min-w-0">
                  <span className="muted-strong">{c.after.key}</span>
                  <span className="muted">: </span>
                  <ChangeText from={c.before.value} to={c.after.value} />
                </span>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ResponseViewer({
  response,
  error,
  pending,
  requestId,
  workspaceId,
}: {
  response: RunResult | null;
  error: string | null;
  pending: boolean;
  requestId: string;
  workspaceId: string;
}) {
  void requestId; void workspaceId;
  const [tab, setTab] = useState<"body" | "headers" | "request">("body");
  if (pending && !response) {
    return <div className="grid h-full place-items-center"><InlineLoading label="Sending request…" /></div>;
  }
  if (!response && !error) {
    return (
      <div className="grid h-full place-items-center muted text-sm">
        <div className="text-center">
          <div className="mx-auto mb-2 h-10 w-10 rounded-full bg-surface-2 grid place-items-center text-text-faint">↩</div>
          <p>Press <span className="kbd">Send</span> to run this request.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="border-b border-border bg-surface px-3 py-2 flex items-center gap-3 flex-wrap">
        <StatusPill status={response?.status ?? null} error={response?.error ?? error} />
        {response && (
          <>
            <span className="text-xs muted-strong mono">{response.durationMs} ms</span>
            {response.size != null && (
              <span className="text-xs muted-strong mono">{formatBytes(response.size)}</span>
            )}
            {response.contentType && (
              <span className="text-xs muted-strong mono truncate">{response.contentType}</span>
            )}
          </>
        )}
        <div className="ml-auto flex gap-1">
          <TabBtn active={tab === "body"} onClick={() => setTab("body")}>Body</TabBtn>
          <TabBtn active={tab === "headers"} onClick={() => setTab("headers")}>Headers</TabBtn>
          <TabBtn active={tab === "request"} onClick={() => setTab("request")}>Request</TabBtn>
        </div>
      </div>
      {/* Body tab fills the height and scrolls INTERNALLY (so the
          JSON filter bar stays pinned at the bottom); the other tabs
          scroll the whole pane as before. */}
      <div className="flex-1 min-h-0 flex flex-col">
        {response?.error || error ? (
          <div className="overflow-auto scroll-pane">
            <div className="m-3 rounded-md border border-danger/40 bg-danger/10 p-3">
              <div className="text-xs uppercase tracking-wider muted-strong mb-1">Request failed</div>
              <pre className="response-body text-danger whitespace-pre-wrap">{prettyAgentError(response?.error ?? error ?? "")}</pre>
              {response?.request && (
                <div className="mt-3"><SentRequestPanel request={response.request} /></div>
              )}
            </div>
          </div>
        ) : tab === "body" ? (
          <div className="flex-1 min-h-0 p-3"><ResponseBody body={response?.body ?? ""} contentType={response?.contentType ?? null} /></div>
        ) : tab === "headers" ? (
          <div className="flex-1 min-h-0 overflow-auto scroll-pane p-3"><HeaderList headers={response?.headers ?? null} /></div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto scroll-pane p-3">
            {response?.request
              ? <SentRequestPanel request={response.request} />
              : <p className="muted text-sm">No sent-request info available.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// Renders what we actually put on the wire (after env-var substitution
// and any form/multipart encoding). Useful for debugging headers, content
// types, and the final URL when an env var didn't resolve as expected.
function SentRequestPanel({ request }: { request: NonNullable<RunResult["request"]> }) {
  const ct = findCt(request.headers);
  const bodyLang: SyntaxLang = useMemo(() => detectLangFromContentType(ct), [ct]);
  const prettyBody = useMemo(() => {
    if (!request.body) return "";
    return bodyLang === "json" ? tryPrettyJson(request.body) : request.body;
  }, [request.body, bodyLang]);
  return (
    <div className="space-y-3 min-w-0">
      <div>
        <div className="label mb-1">Sent</div>
        <div className="flex items-center gap-2 min-w-0">
          <span className={"method-tag shrink-0 " + request.method.toLowerCase()}>{request.method}</span>
          <code className="text-xs font-mono truncate flex-1 min-w-0" title={request.url}>{request.url}</code>
        </div>
      </div>
      <div className="min-w-0">
        <div className="label mb-1">Request headers</div>
        <HeaderList headers={request.headers} />
      </div>
      {request.body != null && request.body !== "" && (
        <div className="min-w-0">
          <div className="label mb-1 flex items-center gap-2">
            <span>Request body</span>
            <span className="text-[10px] muted-strong mono">{request.bodyType}</span>
          </div>
          {bodyLang === "plain" ? (
            <pre className="response-body whitespace-pre-wrap break-words">{request.body}</pre>
          ) : (
            <div className="h-64 min-w-0">
              <CodeViewer value={prettyBody} lang={bodyLang} maxHeight="100%" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
function findCt(headers: Record<string, string>): string | null {
  for (const [k, v] of Object.entries(headers)) if (k.toLowerCase() === "content-type") return v;
  return null;
}

// Make raw agent errors a little friendlier. We see strings like
// "agent_dispatch_failed:503" or "agent_offline" come back from the worker
// → translate to a one-line cause + hint.
function prettyAgentError(s: string): string {
  if (!s) return "";
  if (s.includes("agent_offline")) return "Agent is offline. Start your local rest-agent or pick a different executor.";
  if (s.startsWith("agent_dispatch_failed")) return "Couldn't reach the agent — connection dropped before the request returned.";
  if (s.includes("agent_timeout")) return "Agent didn't reply in time. Check the local terminal where rest-agent is running.";
  return s;
}

// Resolved-URL preview: shown below the URL bar when an environment is
// selected AND the URL actually contains {{var}} references. Highlights
// unresolved variables in red so it's obvious before you hit Send.
function ResolvedUrlPreview({ url, env }: { url: string; env: { vars: Record<string, string> } | undefined | null }) {
  if (!env) return null;
  const refs = findVars(url, env.vars);
  if (!refs.length) return null;
  const unresolved = refs.filter((r) => r.resolved === undefined);
  const resolved = interpolate(url, env.vars);
  return (
    <div className="px-3 py-1.5 border-b border-border bg-surface-2 text-xs flex items-center gap-2 overflow-x-auto">
      <span className="muted-strong shrink-0">resolves to</span>
      <code className="mono truncate">{resolved}</code>
      {unresolved.length > 0 && (
        <span className="chip bg-danger/15 text-danger shrink-0" title={`Missing: ${unresolved.map((u) => u.name).join(", ")}`}>
          {unresolved.length} unresolved
        </span>
      )}
    </div>
  );
}

type BodyView = "formatted" | "raw" | "preview";

function ResponseBody({ body, contentType }: { body: string; contentType: string | null }) {
  const lang = useMemo<SyntaxLang>(() => detectLangFromContentType(contentType), [contentType]);
  // Default: open the formatted view when we can pick a syntax for it,
  // otherwise drop straight to raw. Preview is the third option for
  // HTML content type so the user can see a rendered version.
  const supportsPreview = lang === "html";
  const [view, setView] = useState<BodyView>(lang === "plain" ? "raw" : "formatted");

  // Zero-byte bodies (204 No Content, HEAD requests, plain empty POSTs)
  // shouldn't render as a stark white pre — show a friendly placeholder.
  if (!body || body.length === 0) {
    return (
      <div className="empty-card">No body returned (0 bytes).</div>
    );
  }

  // Pretty-print only for JSON. Other syntaxes are usually already
  // well-formatted from the server.
  const formatted = useMemo(() => {
    if (lang === "json") return tryPrettyJson(body);
    return body;
  }, [body, lang]);

  // JSONPath filter bar — type `$.items[0].name` and the formatted view
  // narrows to that slice in realtime. Empty filter shows everything.
  const [filter, setFilter] = useState("");
  const parsedJson = useMemo(() => {
    if (lang !== "json") return undefined;
    try { return JSON.parse(body) as unknown; } catch { return undefined; }
  }, [body, lang]);
  const filterResult = useMemo(() => {
    if (lang !== "json" || !filter.trim() || parsedJson === undefined) return null;
    return evalJsonPath(parsedJson, filter);
  }, [filter, parsedJson, lang]);
  const displayValue = filterResult?.ok
    ? JSON.stringify(filterResult.value, null, 2) ?? "undefined"
    : formatted;

  return (
    <div className="flex flex-col gap-2 h-full min-h-0">
      <div className="flex items-center gap-1 flex-wrap">
        <ViewTab active={view === "formatted"} onClick={() => setView("formatted")} disabled={lang === "plain"}>
          Formatted
        </ViewTab>
        <ViewTab active={view === "raw"} onClick={() => setView("raw")}>Raw</ViewTab>
        {supportsPreview && (
          <ViewTab active={view === "preview"} onClick={() => setView("preview")}>Preview</ViewTab>
        )}
        <span className="ml-auto text-[11px] muted-strong mono">{lang === "plain" ? "text" : lang}</span>
      </div>
      <div className="flex-1 min-h-0 min-w-0 overflow-auto scroll-pane">
        {view === "formatted" && lang !== "plain" && (
          <CodeViewer value={displayValue} lang={lang} maxHeight="100%" />
        )}
        {(view === "raw" || lang === "plain") && (
          <pre className="response-body whitespace-pre-wrap break-words">{body}</pre>
        )}
        {view === "preview" && supportsPreview && (
          <HtmlPreview html={body} />
        )}
      </div>
      {view === "formatted" && lang === "json" && parsedJson !== undefined && (
        <div className="flex items-center gap-2 shrink-0">
          <input
            className="input input-mono flex-1 !text-xs"
            placeholder="Filter — e.g. $.items[0].name or $.data[*].id"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            spellCheck={false}
            aria-label="JSONPath filter"
          />
          {filter.trim() && filterResult && (
            filterResult.ok ? (
              <span className="chip bg-good/15 text-good shrink-0">
                {filterResult.matches === 1 ? "1 match" : `${filterResult.matches} matches`}
              </span>
            ) : (
              <span className="chip bg-danger/15 text-danger shrink-0" title={filterResult.error}>
                {filterResult.error}
              </span>
            )
          )}
          {filter.trim() && (
            <button type="button" className="btn !px-2 text-xs shrink-0" onClick={() => setFilter("")} aria-label="Clear filter">×</button>
          )}
        </div>
      )}
    </div>
  );
}

function ViewTab({ active, onClick, disabled, children }: { active: boolean; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        "px-2 py-0.5 rounded-sm text-xs font-medium transition " +
        (active
          ? "bg-accent text-[var(--ink-on-accent)]"
          : disabled
            ? "bg-surface-2 text-text-faint cursor-not-allowed"
            : "bg-surface-2 text-text-dim hover:bg-surface-3 hover:text-text")
      }
    >{children}</button>
  );
}

// Render server-returned HTML in a sandboxed iframe so its scripts can't
// touch our window. allow-same-origin is intentionally OFF — we want
// scripts to fail rather than have access to the parent document.
function HtmlPreview({ html }: { html: string }) {
  return (
    <iframe
      title="Response preview"
      sandbox=""
      srcDoc={html}
      className="w-full h-full min-h-[240px] rounded border border-border bg-white"
    />
  );
}

function TestsList({ tests }: { tests: TestResult[] }) {
  if (!tests.length) return <p className="muted text-sm">No tests ran.</p>;
  return (
    <ul className="space-y-1">
      {tests.map((t, i) => (
        <li key={i} className="flex items-start gap-3 rounded-md border border-border bg-surface-2 p-2.5 text-sm">
          <span className={"chip mt-0.5 " + (t.status === "pass" ? "bg-good/15 text-good" : t.status === "fail" ? "bg-danger/15 text-danger" : "bg-surface-3 muted-strong")}>{t.status}</span>
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{t.name}</div>
            {t.message && <div className="text-xs muted-strong break-words">{t.message}</div>}
          </div>
          <span className="text-xs muted-strong mono">{t.durationMs}ms</span>
        </li>
      ))}
    </ul>
  );
}

function HeaderList({ headers }: { headers: Record<string, string> | null }) {
  if (!headers) return <p className="muted text-sm">No headers.</p>;
  const entries = Object.entries(headers);
  if (entries.length === 0) return <p className="muted text-sm">No headers.</p>;
  return (
    <ul className="text-xs mono space-y-1">
      {entries.map(([k, v]) => (
        <li key={k} className="grid grid-cols-[max-content_1fr] gap-3">
          <span className="text-text-dim">{k}:</span>
          <span className="break-all">{v}</span>
        </li>
      ))}
    </ul>
  );
}

function StatusPill({ status, error }: { status: number | null; error: string | null }) {
  if (error) return <span className="status-pill serr">ERROR</span>;
  if (status == null) return <span className="status-pill serr">—</span>;
  const band = status >= 500 ? "s5xx" : status >= 400 ? "s4xx" : status >= 300 ? "s3xx" : "s2xx";
  return <span className={`status-pill ${band}`}>{status}</span>;
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-2 py-1 text-xs font-medium rounded " +
        (active ? "bg-surface-3 text-text" : "text-text-dim hover:bg-surface-2 hover:text-text")
      }
    >{children}</button>
  );
}

function RequestMenu({ onDelete }: { onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 100)}
        className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
        aria-label="Request menu"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="5" cy="12" r="1.5" fill="currentColor"/><circle cx="12" cy="12" r="1.5" fill="currentColor"/><circle cx="19" cy="12" r="1.5" fill="currentColor"/></svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 z-40 text-sm w-48" role="menu">
          <button
            className="w-full text-left px-2 py-1 rounded hover:bg-surface-2 text-danger"
            onMouseDown={(e) => { e.preventDefault(); onDelete(); setOpen(false); }}
          >Delete</button>
        </div>
      )}
    </div>
  );
}

// Primary Send + dropdown rendered as ONE button visually: a single
// background, single border, single border-radius. Inside it, two
// transparent click regions share the surface — hovering either half
// brightens the whole pill, so it never feels like two adjacent buttons.
// The hairline divider between them is a subtle inner shadow, only visible
// as a faint groove like macOS split buttons.
function SendButton({ pending, disabled, disabledReason = null, onSend, onCopyCurl, onShowCode, className = "" }: {
  pending: boolean;
  disabled: boolean;
  /** One-line explanation surfaced as a tooltip when the button is
   *  disabled. Null means just visually-disabled with no extra hint. */
  disabledReason?: string | null;
  onSend: () => void;
  onCopyCurl: () => void;
  onShowCode: () => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [hoverTip, setHoverTip] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  const inert = disabled || pending;
  // When the button is disabled (no URL etc.), the chevron should be
  // dead too — opening a "Copy as curl" menu on a request you can't run
  // sends a confusing message. We keep them in lockstep here.
  const auxDisabled = inert;
  return (
    <div
      ref={wrap}
      className={
        "split-btn relative inline-flex select-none " +
        (inert ? "is-disabled " : "") +
        className
      }
      onMouseEnter={() => inert && disabledReason && setHoverTip(true)}
      onMouseLeave={() => setHoverTip(false)}
      onFocus={() => inert && disabledReason && setHoverTip(true)}
      onBlur={() => setHoverTip(false)}
    >
      <button
        type="button"
        onClick={onSend}
        disabled={inert}
        aria-busy={pending || undefined}
        aria-describedby={inert && disabledReason ? "send-disabled-reason" : undefined}
        className="split-btn-main flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-semibold"
      >
        {pending && <Spinner />}
        <span>{pending ? "Sending…" : "Send"}</span>
      </button>
      <button
        type="button"
        aria-label={auxDisabled ? (disabledReason ?? "Send options") : "Send options"}
        disabled={auxDisabled}
        onClick={() => { if (auxDisabled) return; setOpen((v) => !v); }}
        className="split-btn-aux flex items-center justify-center px-2.5 py-1.5"
      >
        <ChevronDownIcon />
      </button>
      {open && !auxDisabled && (
        <div role="menu" className="absolute right-0 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 z-40 text-sm w-56">
          <MenuItem onSelect={() => { setOpen(false); onCopyCurl(); }}>Copy as curl</MenuItem>
          <MenuItem onSelect={() => { setOpen(false); onShowCode(); }}>Show in code…</MenuItem>
        </div>
      )}
      {hoverTip && inert && disabledReason && (
        <div
          id="send-disabled-reason"
          role="tooltip"
          className="send-disabled-tip absolute right-0 top-full mt-2 z-50"
        >
          {disabledReason}
        </div>
      )}
    </div>
  );
}

function MenuItem({ onSelect, children }: { onSelect: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      className="w-full text-left px-2 py-1.5 rounded hover:bg-surface-2 text-text"
      onMouseDown={(e) => { e.preventDefault(); onSelect(); }}
    >{children}</button>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}


function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
